import { FC, useState, Fragment, useEffect } from "react";
import styled from "styled-components";
import Link from "next/link";
import Image from "next/image";
import { decode } from "shopify-gid";
import type { Product, ProductVariant } from "shopify-storefront-api-typings";
import { ContentfulProductWithShopifyData } from "@lib/types";

import { useDictionary, useCustomer, useUI, useTracking } from "@hooks/index";

import {
  ColorSelector,
  SizeSelector,
  ProductAccordion,
  ProductRating,
} from "./index";
import { RichText, WistiaHeroVideo, Price } from "@components/common";
import Heart from "@svg/heart.svg";
import { ShortReview } from "@components/ui";

interface Props {
  page: ContentfulProductWithShopifyData;
  shopifyProduct: Product;
  total_reviews: number;
}

const ProductInfo: FC<Props> = ({ page, shopifyProduct, total_reviews }) => {
  const {
    slug,
    title,
    description,
    uspCollection,
    texture,
    thickness,
    sizeChart,
    materialsCare,
    wistiaId,
    color,
    category,
    productFamily,
  } = page;

  const dictionary = useDictionary();
  const { openLoginModal } = useUI();
  const { track, tikTokTrack } = useTracking();
  const { currency, customer, wishlist, addToWishlist, deleteFromWishlist } =
    useCustomer();

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    null
  );

  const variantId = selectedVariant ? decode(selectedVariant?.id).id : null;

  const trackingInfo = {
    name: title,
    url: `https://www.hautehijab.com/products/${slug}`,
    category: category?.name,
    brand: "Haute Hijab",
  };

  useEffect(() => {
    if (!variantId) return;
    track("Product Viewed", {
      ...trackingInfo,
      url: `https://www.hautehijab.com/${slug}`,
      currency,
      sku: selectedVariant?.sku,
      product_id: variantId,
      price: parseFloat(selectedVariant?.priceV2?.amount),
    });
    tikTokTrack("ViewContent", {
      content_category: "Product Viewed",
      content_name: selectedVariant?.title,
      content_id: shopifyProduct?.handle
    });
  }, [slug, variantId]);

  useEffect(() => {
    if (shopifyProduct?.handle === "gift-card") {
      // temporary fix for gift cards oos from backend inventory
      shopifyProduct.variants.edges.forEach(
        (edge, i) => (edge.node.availableForSale = true)
      );
    }

    if (shopifyProduct?.variants?.edges?.length === 1) {
      setSelectedVariant(shopifyProduct.variants.edges[0].node);
    }

    return () => {
      setSelectedVariant(null);
    };
  }, [shopifyProduct]);

  const family = productFamily?.productsCollection?.items;

  const breadCrumbs = [
    { title: "home", href: "" },
    { title: category?.category?.name, href: category?.category?.slug },
    { title: category?.name, href: category?.slug },
  ];

  const inWishlist = wishlist?.some(
    ({ id }: { id: string }) => id === selectedVariant?.id
  );

  const handleWishlistClick = () => {
    const trackWishlist = {
      ...trackingInfo,
      product_id: decode(selectedVariant?.id).id,
      variant: selectedVariant?.title,
      sku: selectedVariant?.sku,
      price: parseFloat(selectedVariant?.priceV2?.amount),
      quantity: 1,
      image_url: selectedVariant?.image?.transformedSrc,
    };

    if (inWishlist) {
      deleteFromWishlist(selectedVariant?.id);
      track("Product Removed from Wishlist", {
        ...trackWishlist,
      });
    } else {
      if (!customer) {
        openLoginModal(true);
        return;
      }

      addToWishlist({ ...selectedVariant, productTitle: title });
      const currencyAdjustedPrice = selectedVariant?.presentmentPrices.edges.find((edge: {node: {price: {currencyCode: string}}}) => edge.node.price.currencyCode === currency)?.node.price.amount || null;

      track("Product Added to Wishlist", {
        ...trackWishlist,
      });
      tikTokTrack("AddToWishlist", {
        content_type: "product",
        content_id: decode(selectedVariant?.id).id,
        content_name: selectedVariant?.title,
        currency: currency,
        price: currencyAdjustedPrice,
        value: currencyAdjustedPrice,
        quantity: 1
      })
    }
  };

  const allVariants = shopifyProduct?.variants.edges;
  const firstVariant = allVariants && allVariants[0];
  const lastVariant = allVariants && allVariants[allVariants.length - 1];
  const productHasMultiplePrices =
    firstVariant?.node.priceV2.amount !== lastVariant?.node.priceV2.amount;

  useEffect(() => {
    if(shopifyProduct) {
      try {
        // @ts-ignore
        window.productReadyEvent(shopifyProduct);
      } catch (err) {
      }
    }
  }, [shopifyProduct])

  return (
    <StyledProductInfo>
      <Header>
        <BreadCrumbs>
          {breadCrumbs.map(({ title, href }, i) => {
            return (
              <Fragment key={`${title}${i}`}>
                {i === 0 ? "" : " - "}
                <Link href={`/${href}`} passHref>
                  <a>{title}</a>
                </Link>
              </Fragment>
            );
          })}
        </BreadCrumbs>
        <TitleWrapper>
          <Title>{title}</Title>
          <PriceWrapper>
            {shopifyProduct && (
              <Price
                variant={
                  selectedVariant
                    ? selectedVariant
                    : shopifyProduct.variants.edges[0].node
                }
                isRange={selectedVariant ? false : productHasMultiplePrices}
              />
            )}
          </PriceWrapper>
        </TitleWrapper>
        <ReviewWrapper>
          <ShortReview total_reviews={total_reviews} />
          <Wishlist
            type="button"
            disabled={!selectedVariant}
            onClick={handleWishlistClick}
          >
            <StyledHeart $fill={inWishlist} />
            {inWishlist
              ? dictionary.removeFromWishlist
              : dictionary.addToWishlist}
          </Wishlist>
        </ReviewWrapper>
      </Header>
      {family && <ColorSelector selected={color?.name} products={family} />}
      <SizeSelectorWrapper>
        {shopifyProduct && (
          <SizeSelector
            trackingInfo={trackingInfo}
            variants={shopifyProduct.variants.edges}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            sizeChart={sizeChart}
          />
        )}
      </SizeSelectorWrapper>
      {description && (
        <Description>
          <RichText richText={description} />
        </Description>
      )}
      <Usps>
        {uspCollection?.items?.map(({ image, text }) => {
          return (
            <Usp key={text}>
              <Image
                src={image?.url}
                alt={image?.description}
                width={20}
                height={20}
                layout="fixed"
              />
              <p>{text}</p>
            </Usp>
          );
        })}
      </Usps>
      {(texture || thickness) && (
        <ProductAccordion label={dictionary.fabricGuide}>
          {texture && (
            <ProductRating
              title={dictionary.texture}
              rating={texture}
              values={[
                dictionary.smooth,
                dictionary.slightlyTextured,
                dictionary.textured,
              ]}
            />
          )}
          {thickness && (
            <ProductRating
              title={dictionary.thickness}
              rating={thickness}
              values={[dictionary.light, dictionary.medium, dictionary.heavy]}
            />
          )}
        </ProductAccordion>
      )}
      {materialsCare?.length > 0 && (
        <ProductAccordion label={dictionary.materialsCare}>
          <List>
            {materialsCare?.map((text) => {
              return (
                <ListItem key={text}>
                  <div />
                  {text}
                </ListItem>
              );
            })}
          </List>
        </ProductAccordion>
      )}
      {wistiaId && (
        <ProductAccordion label={dictionary.howToWearIt}>
          <VideoWrapper>
            <WistiaHeroVideo wistia_id={wistiaId} autoPlay={false} />
          </VideoWrapper>
        </ProductAccordion>
      )}
    </StyledProductInfo>
  );
};

export default ProductInfo;

const StyledProductInfo = styled.div`
  padding: 0 2rem 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    position: sticky;
    top: 1rem;
    padding: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    max-width: 45rem;
  }
`;

const Header = styled.div`
  padding: 2.3rem 0 1.8rem;
  border-bottom: 1px solid ${({ theme }) => theme.color.greyLight};
`;

const BreadCrumbs = styled.div`
  text-transform: uppercase;
  font-size: 1.1rem;
  line-height: 2.4rem;
  letter-spacing: 1.67px;
  color: ${({ theme }) => theme.color.greyBlack};

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 0 0.5rem;
  }
`;

const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 2rem;
  margin: 0 0 1.2rem;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 400;
  font-size: 2.4rem;
  line-height: 4.3rem;
  letter-spacing: 0.04em;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    font-size: 3rem;
  }
`;

const PriceWrapper = styled.div`
  margin-top: 0.5rem;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 2.4rem;
  font-weight: 300;
  line-height: 3.6rem;
  justify-self: end;
  text-align: right;
`;

const ReviewWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Wishlist = styled.button`
  display: flex;
  align-items: center;
  font-family: ${({ theme }) => theme.font.heading};
  text-decoration: underline;
  font-size: 1.6rem;
  font-weight: 300;
  line-height: 2.4rem;
  color: ${({ theme }) => theme.color.grey};
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.color.greyLight};
    cursor: default;

    svg {
      path {
        stroke: ${({ theme }) => theme.color.greyLight};
      }
    }
  }

  svg {
    width: 1.6rem;
    margin-right: 0.6rem;
  }
`;

const Description = styled.div`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 1.6rem;
  line-height: 2.4rem;
  font-weight: 300;

  b {
    font-weight: 400;
  }
`;

const Usps = styled.div`
  padding: 0 0 2rem;
`;

const Usp = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 2rem;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 1.6rem;
  font-weight: 300;
  line-height: 1.7rem;

  p {
    margin: 0 0 0 1.7rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0;
    line-height: 3.2rem;
  }
`;

const List = styled.ul`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 1.6rem;
  font-weight: 300;
  line-height: 1.6rem;
  padding-left: 0.3rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    line-height: 3.2rem;
  }
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin: 0 0 1.5rem;

  div {
    flex-shrink: 0;
    margin: 0.6rem 1.5rem 0 0;
    width: 0.5rem;
    height: 0.5rem;
    background: ${({ theme }) => theme.color.black};
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0;

    div {
      margin: 1.3rem 1rem 0 0;
    }
  }
`;

const VideoWrapper = styled.div`
  height: 18.2rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    height: 25rem;
  }
`;

const StyledHeart = styled(Heart)<{ $fill: boolean }>`
  path {
    stroke: black;
    fill: ${({ theme, $fill }) =>
      $fill ? theme.color.black : theme.color.white};
  }
`;

const SizeSelectorWrapper = styled.div`
  padding: 1.4rem 0 2rem;
`;
